import { Document, HeadingLevel, Packer, Paragraph, TextRun } from 'docx';
import { Children, useCallback, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'react-toastify';

import { useDataRoom } from '@/contexts/overview/dataroom/utils';
import { ScrollableDiv } from '@/pages/overview/common/ScrollableDiv';
import NoCompanyOverviewData from '@/pages/overview/company-overview/common/NoCompanyOverviewData';
import { CompanyOverviewData } from '@/pages/overview/company-overview/CompanyOverview';
import { SourceFilePdfViewer } from '@/pages/pdf-viewer/SourceFilePdfViewer';

interface CompanyOverviewContentProps {
  companyOverviewData: CompanyOverviewData;
}

/**
 * Decode the `companyOverviewData.report` string so it renders as clean Markdown
 * *without* relying on external remark/rehype plugins.  We:
 *   1. Peel away up to three JSON‑encodings / quote wrappers.
 *   2. Un‑escape common control characters (\n, \t, etc.).
 *   3. Replace <strong>…</strong> with **…** so bold text survives even though
 *      we aren't allowing raw HTML.
 */
const decodeReport = (input: string): string => {
  if (!input) return '';

  let raw: unknown = input.trim();

  // 1️⃣  Remove nested JSON/string wrappers while result stays a string
  for (let i = 0; i < 3; i++) {
    if (typeof raw !== 'string') break;

    // Strip one layer of surrounding quotes first
    if ((raw.startsWith('"') && raw.endsWith('"')) || (raw.startsWith("'") && raw.endsWith("'"))) {
      raw = (raw as string).slice(1, -1);
    }

    try {
      const parsed = JSON.parse(raw as string);
      if (typeof parsed === 'string') {
        raw = parsed;
        continue;
      }
      break; // parsed into non‑string ⇒ stop
    } catch {
      break; // not valid JSON
    }
  }

  if (typeof raw !== 'string') raw = JSON.stringify(raw, null, 2);

  // 2️⃣  Un‑escape control characters (double‑escaped first, then single)
  const unescape = (s: string) =>
    s
      .replace(/\\\\n/g, '\n')
      .replace(/\\\\r/g, '\r')
      .replace(/\\\\t/g, '\t')
      .replace(/\\n/g, '\n')
      .replace(/\\r/g, '\r')
      .replace(/\\t/g, '\t')
      .replace(/\t/g, '    ');

  let md = unescape(raw as string);

  // Convert \uXXXX sequences (e.g., \u2019 → ’)
  md = md.replace(/\\u([\dA-Fa-f]{4})/g, (_, h) => String.fromCharCode(parseInt(h, 16)));

  // 3️⃣  Convert <strong> tags to **bold** (ReactMarkdown escapes raw HTML)
  md = md.replace(/<strong>(.*?)<\/strong>/gi, '**$1**');

  return md.trim();
};

const parseCitationText = (
  text: string,
  citations: Record<string, string>,
  selectSourceFile: (doc: string) => void,
) => {
  const citationBlockRegex = /\[([^\]]+)\]/g;

  const parts: (string | JSX.Element)[] = [];
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = citationBlockRegex.exec(text)) !== null) {
    const index = match.index;
    const full = match[0];
    const inside = match[1];

    // literal text that precedes this citation block
    if (index > last) {
      parts.push(text.slice(last, index));
    }

    const items = inside.match(/\d+\s*(?:\([^)]+\))?/g) || [];

    parts.push(
      <span key={index}>
        [
        {items.map((item, i) => {
          const prefix = i ? ', ' : '';
          const m = item.match(/^(\d+)(?:\s*\(([^)]*)\))?$/);

          if (!m) return prefix + item;

          const id = m[1];
          const label = m[2];
          const docName = citations[id];

          return (
            <span key={`${index}-${id}`}>
              {prefix}
              {docName ? (
                <a
                  href="#"
                  className="text-blue-500 hover:underline"
                  onClick={(e) => {
                    e.preventDefault();
                    selectSourceFile(docName);
                  }}
                >
                  {id}
                </a>
              ) : (
                id
              )}
              {label && ` (${label})`}
            </span>
          );
        })}
        ]
      </span>,
    );

    last = index + full.length;
  }

  if (last < text.length) {
    parts.push(text.slice(last));
  }

  return parts;
};

/** Recursively convert every plain‑string child into a parsed citation block */
const mapChildren = (
  children: React.ReactNode,
  citations: Record<string, string>,
  selectSourceFile: (doc: string) => void,
): React.ReactNode =>
  Children.map(children, (child) =>
    typeof child === 'string' ? parseCitationText(child, citations, selectSourceFile) : child,
  );

// Custom renderers for headings so they appear bold and larger without extra packages
const createMarkdownComponents = (
  citations: Record<string, string>,
  selectSourceFile: (document: string) => void,
) => ({
  h1: ({ ...props }: React.ComponentPropsWithoutRef<'h1'>) => (
    <h1 className="mb-2 mt-4 text-[14px] font-bold" {...props} />
  ),
  h2: ({ ...props }: React.ComponentPropsWithoutRef<'h2'>) => (
    <h2 className="mb-1 mt-3 text-[14px] font-bold" {...props} />
  ),
  h3: ({ ...props }: React.ComponentPropsWithoutRef<'h3'>) => (
    <h3 className="mb-1 mt-2 text-[14px] font-bold" {...props} />
  ),
  ul: ({ ...props }: React.ComponentPropsWithoutRef<'ul'>) => (
    <ul className="ml-6 list-inside list-disc text-[14px]" {...props} />
  ),
  ol: ({ ...props }: React.ComponentPropsWithoutRef<'ol'>) => (
    <ol className="ml-6 list-inside list-decimal text-[14px]" {...props} />
  ),
  li: ({ children, ...props }: React.ComponentPropsWithoutRef<'li'>) => (
    <li className="mb-1 text-[14px]" {...props}>
      {mapChildren(children, citations, selectSourceFile)}
    </li>
  ),
  p: ({ children, ...props }: React.ComponentPropsWithoutRef<'p'>) => (
    <p className="text-[14px]" {...props}>
      {mapChildren(children, citations, selectSourceFile)}
    </p>
  ),
});

const convertMarkdownToDocxElements = (markdown: string) => {
  const lines = markdown.split('\n');
  const elements: Paragraph[] = [];

  lines.forEach((line) => {
    const trimmedLine = line.trim();

    // Handle headings
    if (trimmedLine.startsWith('# ')) {
      elements.push(
        new Paragraph({
          children: [
            new TextRun({
              text: trimmedLine.substring(2),
              size: 36,
              bold: true,
              color: '000000',
            }),
          ],
        }),
      );
      return;
    }

    if (trimmedLine.startsWith('## ')) {
      elements.push(
        new Paragraph({
          children: [
            new TextRun({
              text: trimmedLine.substring(3),
              size: 30,
              bold: true,
              color: '000000',
            }),
          ],
        }),
      );
      return;
    }
    if (trimmedLine.startsWith('### ')) {
      elements.push(
        new Paragraph({
          children: [
            new TextRun({
              text: trimmedLine.substring(3),
              size: 26,
              bold: true,
              color: '000000',
            }),
          ],
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 0, after: 0 },
        }),
      );
      return;
    }

    // Handle bold text with bullet points
    if (trimmedLine.includes('**') && trimmedLine.startsWith('- **')) {
      const parts = trimmedLine.substring(2).split('**');
      const runs: TextRun[] = [];
      parts.forEach((part, index) => {
        if (index % 2 === 0) {
          if (index === 0) {
            runs.push(new TextRun({ text: '•  ', bold: true, color: '000000' }));
          }
          if (part) runs.push(new TextRun({ text: part, bold: false, color: '000000' }));
        } else {
          runs.push(new TextRun({ text: part, bold: true, color: '000000' }));
        }
      });
      elements.push(
        new Paragraph({
          children: runs,
          indent: { left: 250 },
          spacing: { after: 120 },
        }),
      );
      return;
    }

    // Handle bullet points from markdown
    if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      elements.push(
        new Paragraph({
          children: [
            new TextRun({
              text: '•  ' + trimmedLine.substring(2),
              bold: false,
              color: '000000',
            }),
          ],
          indent: { left: 720 },
          spacing: { after: 120 },
        }),
      );
      return;
    }

    // Regular paragraph
    elements.push(
      new Paragraph({
        children: [
          new TextRun({
            text: trimmedLine,
            bold: true,
            color: '000000',
          }),
        ],
        spacing: { after: 120 },
      }),
    );
  });

  return elements;
};

export const CompanyOverviewContent = ({ companyOverviewData }: CompanyOverviewContentProps) => {
  const { setSelectedFile, selectedFile, dataRoomFiles } = useDataRoom();
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  //Function to select source file based on citation id
  const selectSourceFile = useCallback(
    (citationId: string) => {
      const toSelect = dataRoomFiles.find((file) => {
        const pdfId = file.pdfUrl?.split('/').pop();
        return pdfId === citationId;
      });

      if (toSelect) {
        setSelectedFile(toSelect.name);

        setIsViewerOpen(true);
      }
    },
    [dataRoomFiles, setSelectedFile],
  );

  // Function to close the viewer
  const handleCloseViewer = useCallback(() => {
    setSelectedFile('');
    setIsViewerOpen(false);
  }, [setSelectedFile]);

  // Memoize "Last updated" label
  const formattedLastUpdated = useMemo(() => {
    if (!companyOverviewData?.updatedAt) return '';
    return new Date(companyOverviewData.updatedAt).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
  }, [companyOverviewData?.updatedAt]);

  // Decode markdown whenever raw report changes
  const markdown = useMemo(() => {
    if (!companyOverviewData?.report) return '';
    return decodeReport(companyOverviewData.report);
  }, [companyOverviewData?.report]);

  // Create markdown components with citation support
  const markdownComponentsWithCitations = useMemo(() => {
    return createMarkdownComponents(companyOverviewData.citations ?? {}, selectSourceFile);
  }, [companyOverviewData.citations, selectSourceFile]);

  const exportToDocx = useCallback(async () => {
    if (!markdown) return;

    try {
      const docElements = convertMarkdownToDocxElements(markdown);

      const doc = new Document({
        sections: [
          {
            properties: {},
            children: [...docElements],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'CompanyOverview.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(`Failed to export document. Please try again.`);
    }
  }, [markdown]);

  if (!markdown) return <NoCompanyOverviewData />;

  return (
    <div className="flex size-full cursor-default flex-col items-center rounded-[12px] bg-[#121212] px-[20px] text-[12px]">
      <div className="mb-[56px] mt-[20px] flex w-full items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="flex text-[34px] font-medium text-marveri-white">Company Overview</h1>
          <div
            className="float-end mr-[20px] flex h-[30px] w-[63px] cursor-pointer items-center justify-center rounded-[4px] border border-[#353336] px-[10px] py-3 text-marveri-white hover:bg-[#353336]"
            onClick={exportToDocx}
          >
            Export
          </div>
        </div>
        {formattedLastUpdated && (
          <span className="text-sm text-N400">Last updated: {formattedLastUpdated}</span>
        )}
      </div>

      {/* Render the decoded markdown with citation hyperlinks */}
      <div className="flex w-full gap-3">
        <ScrollableDiv containerStyle="rounded-md border border-N200 bg-white p-6 mb-4 h-[calc(100vh-165px)]">
          <ReactMarkdown components={markdownComponentsWithCitations}>{markdown}</ReactMarkdown>
        </ScrollableDiv>

        <div className="flex grow">
          <div className="h-[98%] min-w-[667px] grow rounded-[12px] bg-[#282829] large:w-[567px]">
            {!!selectedFile && !!isViewerOpen ? (
              <SourceFilePdfViewer
                title={selectedFile.displayName}
                selectedHighlight={[]}
                closeViewer={handleCloseViewer}
                viewerStyle="h-[calc(100vh-285px)]"
              />
            ) : (
              <div className="flex h-full items-center justify-center text-[16px] text-marveri-white">
                Select a Citation{' '}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
